import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  CURRENCY,
  PART_REQUEST_STATUS,
  PRIORITY,
  SHIPPING_CARRIERS,
  type CreatePartInput,
  type CreatePartRequestInput,
  type CreateServiceOrderInput,
  type Part,
  type PartDocType,
  type PartDocument,
  type PartRequest,
  type PartRequestStatus,
  type PartUsageKind,
  type Priority,
  type ServiceOrderStatus,
  type ServiceOrderWithDerived,
} from '@etnrlz/shared';
import {
  useAddComment,
  useAddPartRequest,
  useChangeOrderStatus,
  useCreateOrder,
  useCreatePart,
  useEnrichPartFromLink,
  useExtractInvoice,
  useMovePartRequest,
  usePartRequests,
  usePartRequestToStock,
  useParts,
  useRemoveComment,
  useRemovePartRequest,
  useServiceOrder,
  useServiceOrders,
  useTrackShipment,
  useUpdateChecklist,
  useUpdateOrder,
  useUpdatePartRequest,
  useUsers,
  useVehicles,
  qk,
} from '../../lib/queries';
import { PartSuggest } from '../../components/PartSuggest';
import { PhotoUpload } from '../../components/PhotoUpload';
import {
  Badge,
  Button,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  cx,
} from '../../components/ui';
import { Modal } from '../../components/Modal';
import { Icon } from '../../components/icons';
import { osStatusMeta, partRequestStatusMeta, priorityMeta } from '../../components/status';
import { formatDate, formatDateTime, formatMoney, pluralDays } from '../../lib/format';
import { fileToBase64, uploadFile, uploadOrderPhoto } from '../../lib/upload';

const COLUMNS: ServiceOrderStatus[] = [
  'ABERTA',
  'EM_ANDAMENTO',
  'AGUARDANDO_PECA',
  'PAUSADA',
  'CONCLUIDA',
];

export function OrdersPage({ touch }: { touch?: boolean }) {
  const { data, isLoading } = useServiceOrders();
  const { data: vehicles } = useVehicles();
  const change = useChangeOrderStatus();
  const [openId, setOpenId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // Abre uma OS direto pela URL (?os=<id>) — usado pelo link "Abrir OS" da
  // tela de detalhe da peça (abre em outra aba).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const os = searchParams.get('os');
    if (os) setOpenId(os);
  }, [searchParams]);

  const vmap = useMemo(() => new Map((vehicles ?? []).map((v) => [v.id, v.apelido])), [vehicles]);
  const grouped = useMemo(() => {
    const g: Record<string, ServiceOrderWithDerived[]> = {};
    for (const c of COLUMNS) g[c] = [];
    for (const o of data ?? []) (g[o.status] ??= []).push(o);
    return g;
  }, [data]);

  if (isLoading) return <Loading />;

  return (
    <div>
      <PageHeader
        title="Ordens de serviço"
        subtitle={touch ? 'Toque para mover ou abrir' : 'Arraste os cards entre as colunas'}
        actions={
          <Button onClick={() => setCreateOpen(true)} size={touch ? 'lg' : 'md'}>
            <Icon name="plus" className="h-4 w-4" /> Nova OS
          </Button>
        }
      />

      <div className={cx('flex gap-3 overflow-x-auto pb-4', touch && 'gap-4')}>
        {COLUMNS.map((status) => {
          const meta = osStatusMeta[status];
          const list = grouped[status] ?? [];
          return (
            <div
              key={status}
              className={cx('shrink-0', touch ? 'w-72' : 'w-64')}
              onDragOver={(e) => !touch && e.preventDefault()}
              onDrop={(e) => {
                if (touch) return;
                const id = e.dataTransfer.getData('text/os');
                if (id) change.mutate({ id, status });
              }}
            >
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="flex items-center gap-2 font-medium text-sm">
                  <span className="h-2 w-2 rounded-full" style={{ background: meta.color }} />
                  {meta.label}
                </div>
                <span className="num text-xs text-muted">{list.length}</span>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {list.map((o) => (
                  <article
                    key={o.id}
                    draggable={!touch}
                    onDragStart={(e) => e.dataTransfer.setData('text/os', o.id)}
                    onClick={() => setOpenId(o.id)}
                    className={cx(
                      'card p-3 cursor-pointer hover:border-border-strong transition-colors border-l-4',
                      touch && 'p-4',
                    )}
                    style={{ borderLeftColor: meta.color }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h3 className={cx('font-medium leading-tight', touch && 'text-lg')}>{o.titulo}</h3>
                      <Badge color={priorityMeta[o.prioridade].color}>{priorityMeta[o.prioridade].label}</Badge>
                    </div>
                    <div className="text-xs text-muted mt-1.5 truncate">{vmap.get(o.vehicleId) ?? o.vehicleId}</div>
                    <div className="flex items-center gap-1.5 text-xs text-muted mt-2">
                      <Icon name="clock" className="h-3.5 w-3.5" />
                      <span className="num">{pluralDays(o.diasParado)} parado</span>
                    </div>
                    {touch && status !== 'CONCLUIDA' && (
                      <TouchMove id={o.id} status={status} />
                    )}
                  </article>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {openId && (
        <OrderDetailModal
          id={openId}
          vehicleName={vmap}
          onClose={() => {
            setOpenId(null);
            if (searchParams.has('os')) {
              searchParams.delete('os');
              setSearchParams(searchParams, { replace: true });
            }
          }}
        />
      )}
      <CreateOrderModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

function TouchMove({ id, status }: { id: string; status: ServiceOrderStatus }) {
  const change = useChangeOrderStatus();
  const next: Partial<Record<ServiceOrderStatus, ServiceOrderStatus>> = {
    ABERTA: 'EM_ANDAMENTO',
    EM_ANDAMENTO: 'CONCLUIDA',
    AGUARDANDO_PECA: 'EM_ANDAMENTO',
    PAUSADA: 'EM_ANDAMENTO',
  };
  const target = next[status];
  if (!target) return null;
  return (
    <Button
      size="sm"
      variant="secondary"
      className="w-full mt-3"
      onClick={(e) => {
        e.stopPropagation();
        change.mutate({ id, status: target });
      }}
    >
      → {osStatusMeta[target].label}
    </Button>
  );
}

function ChecklistSection({ order }: { order: ServiceOrderWithDerived }) {
  const update = useUpdateChecklist(order.id);
  const [novo, setNovo] = useState('');
  const list = order.checklist;

  const toggle = (idx: number) =>
    update.mutate(list.map((c, i) => (i === idx ? { ...c, feito: !c.feito } : c)));
  const remove = (idx: number) => update.mutate(list.filter((_, i) => i !== idx));
  const add = () => {
    const item = novo.trim();
    if (!item) return;
    update.mutate([...list, { item, feito: false }]);
    setNovo('');
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted mb-2">Checklist</div>
      {list.length === 0 && <p className="text-sm text-muted mb-2">Nenhum item ainda.</p>}
      <ul className="space-y-1 text-sm mb-2">
        {list.map((c, i) => (
          <li key={i} className="flex items-center gap-2 group">
            <input
              type="checkbox"
              checked={c.feito}
              onChange={() => toggle(i)}
              className="h-4 w-4 accent-accent cursor-pointer shrink-0"
            />
            <span className={c.feito ? 'line-through text-muted flex-1' : 'flex-1'}>{c.item}</span>
            <button
              onClick={() => remove(i)}
              className="text-muted hover:text-st-danger opacity-0 group-hover:opacity-100 px-1"
              aria-label={`Remover ${c.item}`}
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Input
          value={novo}
          onChange={(e) => setNovo(e.target.value)}
          placeholder="Adicionar item ao checklist…"
          onKeyDown={(e) => e.key === 'Enter' && add()}
        />
        <Button size="sm" variant="secondary" onClick={add} disabled={!novo.trim() || update.isPending}>
          Adicionar
        </Button>
      </div>
    </div>
  );
}

function CommentsSection({ order }: { order: ServiceOrderWithDerived }) {
  const add = useAddComment(order.id);
  const remove = useRemoveComment(order.id);
  const [texto, setTexto] = useState('');
  const list = order.comentarios ?? [];

  const submit = () => {
    const t = texto.trim();
    if (!t) return;
    add.mutate(t);
    setTexto('');
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted mb-2">Comentários</div>
      {list.length > 0 && (
        <ul className="space-y-2 mb-2">
          {list.map((c) => (
            <li key={c.id} className="text-sm bg-surface-2 rounded-lg px-3 py-2 group">
              <div className="flex items-center justify-between gap-2 text-xs text-muted mb-0.5">
                <span>
                  {c.por ?? 'Alguém'} · {formatDateTime(c.em)}
                </span>
                <button
                  onClick={() => remove.mutate(c.id)}
                  className="opacity-0 group-hover:opacity-100 hover:text-st-danger px-1"
                  aria-label="Remover comentário"
                >
                  ✕
                </button>
              </div>
              <div className="whitespace-pre-wrap">{c.texto}</div>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Escrever um comentário…"
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && submit()}
        />
        <Button size="sm" variant="secondary" onClick={submit} disabled={!texto.trim() || add.isPending}>
          Comentar
        </Button>
      </div>
    </div>
  );
}

function UsedItemsSection({
  order,
  parts,
  tipo,
  label,
  categoria,
}: {
  order: ServiceOrderWithDerived;
  parts: Part[];
  tipo: PartUsageKind;
  label: string;
  categoria: string;
}) {
  const update = useUpdateOrder();
  const [quickTerm, setQuickTerm] = useState<string | null>(null);
  const partMap = useMemo(() => new Map(parts.map((p) => [p.id, p])), [parts]);
  const all = order.pecasUsadas ?? [];
  const mine = all.filter((u) => (u.tipo ?? 'PECA') === tipo);
  const others = all.filter((u) => (u.tipo ?? 'PECA') !== tipo);
  const excludeIds = new Set(mine.map((u) => u.partId));

  const save = (nextMine: typeof mine) =>
    update.mutate({ id: order.id, input: { pecasUsadas: [...others, ...nextMine] } });
  const add = (partId: string) => {
    if (!excludeIds.has(partId)) save([...mine, { partId, qtd: 1, tipo }]);
  };
  const setQty = (partId: string, qtd: number) =>
    save(mine.map((u) => (u.partId === partId ? { ...u, qtd: Math.max(1, qtd) } : u)));
  const remove = (partId: string) => save(mine.filter((u) => u.partId !== partId));

  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted mb-2">{label}</div>
      {mine.length > 0 && (
        <ul className="space-y-1 text-sm mb-2">
          {mine.map((u) => {
            const p = partMap.get(u.partId);
            const low = p && p.quantidade < p.estoqueMinimo;
            return (
              <li key={u.partId} className="flex items-center gap-2">
                <span className="flex-1 truncate">
                  {p?.nome ?? u.partId}
                  {low && <span className="text-st-danger text-xs ml-1">(estoque baixo)</span>}
                </span>
                <input
                  type="number"
                  min={1}
                  value={u.qtd}
                  onChange={(e) => setQty(u.partId, Number(e.target.value))}
                  className="input-base w-16 py-1"
                />
                <button
                  onClick={() => remove(u.partId)}
                  className="text-muted hover:text-st-danger px-1"
                  aria-label="Remover"
                >
                  ✕
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <PartSuggest
        parts={parts.filter((p) => (p.tipo ?? 'PECA') === tipo)}
        exclude={excludeIds}
        placeholder={`Adicionar ${label.toLowerCase()}…`}
        onSelect={(p) => add(p.id)}
        onCreateNew={(term) => setQuickTerm(term)}
      />
      <QuickAddPartModal
        open={quickTerm !== null}
        term={quickTerm ?? ''}
        categoria={categoria}
        tipo={tipo}
        onClose={() => setQuickTerm(null)}
        onCreated={(p) => {
          add(p.id);
          setQuickTerm(null);
        }}
      />
    </div>
  );
}

function QuickAddPartModal({
  open,
  term,
  categoria,
  tipo,
  onClose,
  onCreated,
}: {
  open: boolean;
  term: string;
  categoria: string;
  tipo: PartUsageKind;
  onClose: () => void;
  onCreated: (part: Part) => void;
}) {
  const create = useCreatePart();
  const [f, setF] = useState<CreatePartInput>({ nome: term, sku: '', tipo, categoria, quantidade: 0, estoqueMinimo: 0 });
  useEffect(() => {
    if (open) setF({ nome: term, sku: '', tipo, categoria, quantidade: 0, estoqueMinimo: 0 });
  }, [open, term, categoria, tipo]);
  const set = (k: keyof CreatePartInput, v: unknown) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    const part = await create.mutateAsync(f);
    onCreated(part);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cadastrar no inventário"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={!f.nome || !f.sku || create.isPending}>
            {create.isPending ? 'Salvando…' : 'Cadastrar e adicionar'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Field label="Nome">
            <Input value={f.nome} onChange={(e) => set('nome', e.target.value)} />
          </Field>
        </div>
        <Field label="SKU">
          <Input value={f.sku} onChange={(e) => set('sku', e.target.value)} placeholder="código interno" />
        </Field>
        <Field label="Categoria">
          <Input value={f.categoria ?? ''} onChange={(e) => set('categoria', e.target.value)} />
        </Field>
        <Field label="Quantidade em estoque">
          <Input type="number" value={f.quantidade ?? 0} onChange={(e) => set('quantidade', Number(e.target.value))} />
        </Field>
        <Field label="Estoque mínimo">
          <Input type="number" value={f.estoqueMinimo ?? 0} onChange={(e) => set('estoqueMinimo', Number(e.target.value))} />
        </Field>
      </div>
    </Modal>
  );
}

/* ------------------------- Solicitação de peças -------------------------- */

const numOrUndef = (v: string): number | undefined => {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const blankPartRequest: CreatePartRequestInput = { status: 'SOLICITADA', quantidade: 1, moeda: 'BRL' };

const partDocTypeLabel: Record<PartDocType, string> = {
  INVOICE: 'Invoice (fornecedor)',
  NOTA_FISCAL: 'Nota fiscal de entrada',
  OUTRO: 'Outro',
};

/** Botão de upload de documento (PDF ou imagem) que anexa um PartDocument. */
function DocUpload({
  tipo,
  label,
  onAdd,
  onExtract,
}: {
  tipo: PartDocType;
  label: string;
  onAdd: (d: PartDocument) => void;
  /** Se presente, roda em paralelo ao upload (ex.: ler a invoice com IA). */
  onExtract?: (file: File) => Promise<void>;
}) {
  const [up, setUp] = useState(false);
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUp(true);
    try {
      const [url] = await Promise.all([
        uploadFile(file, 'part-requests/docs'),
        onExtract ? onExtract(file) : Promise.resolve(),
      ]);
      onAdd({ tipo, nome: file.name, url });
    } finally {
      setUp(false);
      e.target.value = '';
    }
  };
  return (
    <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-surface-2 border border-border hover:border-border-strong cursor-pointer transition-colors">
      {up ? <Spinner className="h-4 w-4" /> : <Icon name="plus" className="h-4 w-4" />}
      {label}
      <input type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} />
    </label>
  );
}

function PartRequestSection({ order }: { order: ServiceOrderWithDerived }) {
  const remove = useRemovePartRequest(order.id);
  const [editing, setEditing] = useState<PartRequest | null>(null);
  const [adding, setAdding] = useState(false);
  const [stocking, setStocking] = useState<PartRequest | null>(null);
  const list = order.pecasSolicitadas ?? [];
  // Total real gasto (custo de entrega no BR), somável por estar todo em BRL.
  const totalEntregue = list.reduce((s, p) => s + (p.valorEntregue ?? 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-muted flex items-center gap-1.5">
          <Icon name="truck" className="h-3.5 w-3.5" /> Solicitação de peças
        </div>
        <button onClick={() => setAdding(true)} className="text-xs text-accent hover:underline">
          + Adicionar peça
        </button>
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-muted">Nenhuma peça solicitada.</p>
      ) : (
        <ul className="space-y-2">
          {list.map((pr) => (
            <PartRequestRow
              key={pr.id}
              pr={pr}
              onEdit={() => setEditing(pr)}
              onRemove={() => remove.mutate(pr.id)}
              onStock={() => setStocking(pr)}
            />
          ))}
        </ul>
      )}

      {totalEntregue > 0 && (
        <div className="flex justify-end text-sm mt-2">
          <span className="text-muted">Total entregue:&nbsp;</span>
          <span className="num font-medium">{formatMoney(totalEntregue)}</span>
        </div>
      )}

      <PartRequestModal
        osId={order.id}
        open={adding || editing !== null}
        initial={editing}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
      />

      {stocking && (
        <StockModal osId={order.id} pr={stocking} open onClose={() => setStocking(null)} />
      )}
    </div>
  );
}

function PartRequestRow({
  pr,
  onEdit,
  onRemove,
  onStock,
}: {
  pr: PartRequest;
  onEdit: () => void;
  onRemove: () => void;
  onStock?: () => void;
}) {
  const meta = partRequestStatusMeta[pr.status];
  return (
    <li className="card p-3 flex gap-3">
      <div className="h-14 w-14 rounded-lg bg-surface-2 overflow-hidden grid place-items-center shrink-0 border border-border">
        {pr.fotoUrl ? (
          <img src={pr.fotoUrl} alt={pr.titulo ?? 'peça'} className="h-full w-full object-cover" />
        ) : (
          <Icon name="box" className="h-5 w-5 text-muted" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="font-medium leading-tight truncate">
            {pr.link ? (
              <a
                href={pr.link}
                target="_blank"
                rel="noreferrer noopener"
                className="hover:text-accent hover:underline"
                title="Abrir link da peça em outra aba"
              >
                {pr.titulo ?? pr.link}
              </a>
            ) : (
              (pr.titulo ?? 'Peça sem título')
            )}
            {pr.quantidade > 1 && <span className="text-muted text-xs ml-1">×{pr.quantidade}</span>}
          </div>
          <Badge color={meta.color}>{meta.label}</Badge>
        </div>
        {pr.descricao && <div className="text-xs text-muted truncate">{pr.descricao}</div>}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted mt-1">
          {pr.valor != null && (
            <span className="num text-text">{formatMoney(pr.valor, pr.moeda)}</span>
          )}
          {pr.frete != null && pr.frete > 0 && (
            <span className="num" title="Frete do fornecedor">
              + frete {formatMoney(pr.frete, pr.moeda)}
            </span>
          )}
          {pr.valorEntregue != null && (
            <span className="num" title="Custo entregue no Brasil">
              entregue {formatMoney(pr.valorEntregue)}
            </span>
          )}
          {(pr.documentos?.length ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1" title="Documentos anexados">
              <Icon name="tag" className="h-3 w-3" /> {pr.documentos!.length} doc
            </span>
          )}
          {pr.orderId && <span className="num" title="OrderID">pedido #{pr.orderId}</span>}
          {pr.link && (
            <a
              href={pr.link}
              target="_blank"
              rel="noreferrer noopener"
              className="text-accent hover:underline inline-flex items-center gap-1"
            >
              <Icon name="link" className="h-3 w-3" /> Link
            </a>
          )}
          {pr.codigoRastreio && (
            <span className="inline-flex items-center gap-1">
              <Icon name="truck" className="h-3 w-3" />
              <span className="num">{pr.codigoRastreio}</span>
              {pr.transportadora ? ` · ${pr.transportadora}` : ''}
            </span>
          )}
          {pr.prazoEntrega && (
            <span className="inline-flex items-center gap-1">
              <Icon name="clock" className="h-3 w-3" /> Prev.: {formatDate(pr.prazoEntrega)}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {pr.status === 'ENTREGUE' &&
          (pr.emEstoque ? (
            <span className="text-[11px] text-st-ok inline-flex items-center gap-1" title="Já lançada no estoque">
              <Icon name="box" className="h-3.5 w-3.5" /> No estoque
            </span>
          ) : (
            onStock && (
              <button
                onClick={onStock}
                className="text-[11px] text-accent hover:underline inline-flex items-center gap-1"
              >
                <Icon name="box" className="h-3.5 w-3.5" /> Em estoque
              </button>
            )
          ))}
        <button onClick={onEdit} className="text-muted hover:text-text px-1 text-xs">
          Editar
        </button>
        <button onClick={onRemove} className="text-muted hover:text-st-danger px-1" aria-label="Remover">
          <Icon name="trash" className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

export function PartRequestModal({
  osId,
  open,
  initial,
  onClose,
}: {
  osId: string;
  open: boolean;
  initial: PartRequest | null;
  onClose: () => void;
}) {
  const add = useAddPartRequest(osId);
  const update = useUpdatePartRequest(osId);
  const enrich = useEnrichPartFromLink();
  const track = useTrackShipment();
  const invoice = useExtractInvoice();
  const [f, setF] = useState<CreatePartRequestInput>(blankPartRequest);
  const [hint, setHint] = useState<string | null>(null);
  // 'new' = cadastrar nova; 'find' = buscar uma peça já comprada e mover pra cá.
  const [mode, setMode] = useState<'new' | 'find'>('new');

  useEffect(() => {
    if (!open) return;
    setHint(null);
    setMode('new');
    enrich.reset();
    track.reset();
    invoice.reset();
    setF(
      initial
        ? {
            titulo: initial.titulo,
            descricao: initial.descricao,
            link: initial.link,
            orderId: initial.orderId,
            valor: initial.valor,
            moeda: initial.moeda,
            valorEntregue: initial.valorEntregue,
            frete: initial.frete,
            quantidade: initial.quantidade,
            codigoRastreio: initial.codigoRastreio,
            transportadora: initial.transportadora,
            fotoUrl: initial.fotoUrl,
            prazoEntrega: initial.prazoEntrega,
            status: initial.status,
            documentos: initial.documentos,
            observacoes: initial.observacoes,
          }
        : blankPartRequest,
    );
  }, [open, initial]);

  const set = (k: keyof CreatePartRequestInput, v: unknown) => setF((s) => ({ ...s, [k]: v }));
  const addDoc = (d: PartDocument) =>
    setF((s) => ({ ...s, documentos: [...(s.documentos ?? []), d] }));
  const removeDoc = (i: number) =>
    setF((s) => ({ ...s, documentos: (s.documentos ?? []).filter((_, j) => j !== i) }));

  const loadFromLink = async () => {
    if (!f.link) return;
    setHint(null);
    try {
      const r = await enrich.mutateAsync({ link: f.link });
      setF((s) => ({
        ...s,
        titulo: r.titulo ?? s.titulo,
        descricao: r.descricao ?? s.descricao,
        valor: r.valor ?? s.valor,
        moeda: r.moeda ?? s.moeda,
        fotoUrl: s.fotoUrl || r.fotoUrl,
      }));
      if (!r.titulo && r.valor == null && !r.fotoUrl) {
        setHint('Não consegui ler os dados do link — preencha manualmente.');
      }
    } catch {
      setHint('Falha ao acessar o link.');
    }
  };

  const calcPrazo = async () => {
    if (!f.codigoRastreio) return;
    setHint(null);
    try {
      const r = await track.mutateAsync({ codigoRastreio: f.codigoRastreio });
      setF((s) => ({
        ...s,
        transportadora: r.transportadora ?? s.transportadora,
        prazoEntrega: r.prazoEntrega ?? s.prazoEntrega,
        status:
          s.status === 'SOLICITADA' || s.status === 'PEDIDA'
            ? ((r.status as PartRequestStatus | undefined) ?? s.status)
            : s.status,
      }));
    } catch {
      setHint('Não foi possível estimar o prazo.');
    }
  };

  // Lê a invoice com IA (Opus 4.8) e preenche OrderID, frete e valores achados.
  const extractFromInvoice = async (file: File) => {
    if (file.size > 4_500_000) {
      setHint('Invoice grande demais para leitura automática (máx. ~4,5 MB) — preencha à mão.');
      return;
    }
    setHint(null);
    try {
      const { data, mediaType } = await fileToBase64(file);
      const r = await invoice.mutateAsync({ data, mediaType, nome: file.name });
      setF((s) => ({
        ...s,
        orderId: r.orderId ?? s.orderId,
        valor: r.valor ?? s.valor,
        frete: r.frete ?? s.frete,
        moeda: (r.moeda as CreatePartRequestInput['moeda']) ?? s.moeda,
        codigoRastreio: r.codigoRastreio ?? s.codigoRastreio,
      }));
      if (r.orderId == null && r.valor == null && r.frete == null) {
        setHint('Não encontrei OrderID/valores na invoice — confira os campos.');
      }
    } catch {
      setHint('Não consegui ler a invoice — preencha os campos manualmente.');
    }
  };

  const submit = async () => {
    if (initial) await update.mutateAsync({ prId: initial.id, input: f });
    else await add.mutateAsync(f);
    onClose();
  };

  const saving = add.isPending || update.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? 'Editar peça solicitada' : 'Adicionar peça'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {initial || mode === 'new' ? 'Cancelar' : 'Fechar'}
          </Button>
          {(initial || mode === 'new') && (
            <Button onClick={submit} disabled={saving}>
              {saving ? 'Salvando…' : initial ? 'Salvar' : 'Adicionar'}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {!initial && (
          <div className="flex gap-2">
            <Button
              variant={mode === 'find' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setMode('find')}
              className="flex-1"
            >
              <Icon name="box" className="h-4 w-4" /> Já comprei (buscar)
            </Button>
            <Button
              variant={mode === 'new' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setMode('new')}
              className="flex-1"
            >
              <Icon name="plus" className="h-4 w-4" /> Nova peça
            </Button>
          </div>
        )}

        {!initial && mode === 'find' ? (
          <FindBoughtPart osId={osId} onMoved={onClose} />
        ) : (
          <>
        <Field label="Link do produto" hint="A IA carrega foto, descrição e valor a partir do link.">
          <div className="flex gap-2">
            <Input
              value={f.link ?? ''}
              onChange={(e) => set('link', e.target.value)}
              placeholder="https://…"
            />
            <Button
              variant="secondary"
              onClick={loadFromLink}
              disabled={!f.link || enrich.isPending}
              title="Carregar com IA"
              className="shrink-0"
            >
              {enrich.isPending ? <Spinner className="h-4 w-4" /> : <Icon name="sparkles" className="h-4 w-4" />}
              Carregar
            </Button>
          </div>
        </Field>

        {hint && <p className="text-xs text-st-danger">{hint}</p>}

        <Field label="OrderID (nº do pedido na loja)">
          <Input
            value={f.orderId ?? ''}
            onChange={(e) => set('orderId', e.target.value || undefined)}
            placeholder="Ex.: 112-3456789-0012345"
          />
        </Field>

        <Field label="Print da peça">
          <PhotoUpload value={f.fotoUrl} onChange={(url) => set('fotoUrl', url)} prefix="part-requests" />
        </Field>

        <Field label="Título">
          <Input
            value={f.titulo ?? ''}
            onChange={(e) => set('titulo', e.target.value)}
            placeholder="Nome da peça"
          />
        </Field>
        <Field label="Descrição">
          <Textarea value={f.descricao ?? ''} onChange={(e) => set('descricao', e.target.value)} />
        </Field>

        <div className="grid grid-cols-3 gap-4">
          <Field label="Valor do produto">
            <Input
              type="number"
              min={0}
              step="0.01"
              value={f.valor ?? ''}
              onChange={(e) => set('valor', numOrUndef(e.target.value))}
            />
          </Field>
          <Field label="Moeda">
            <Select value={f.moeda ?? 'BRL'} onChange={(e) => set('moeda', e.target.value)}>
              {CURRENCY.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Quantidade">
            <Input
              type="number"
              min={1}
              value={f.quantidade ?? 1}
              onChange={(e) => set('quantidade', Math.max(1, Number(e.target.value) || 1))}
            />
          </Field>
        </div>

        <Field label="Frete" hint={`Frete do fornecedor, na moeda do produto (${f.moeda ?? 'BRL'}). A IA lê da invoice.`}>
          <Input
            type="number"
            min={0}
            step="0.01"
            value={f.frete ?? ''}
            onChange={(e) => set('frete', numOrUndef(e.target.value))}
          />
        </Field>

        <Field
          label="Valor entregue (R$)"
          hint="Custo final no Brasil: peça + frete + impostos/desembaraço. Costuma diferir do valor do produto."
        >
          <Input
            type="number"
            min={0}
            step="0.01"
            value={f.valorEntregue ?? ''}
            onChange={(e) => set('valorEntregue', numOrUndef(e.target.value))}
          />
        </Field>

        <Field label="Código de rastreio" hint="A IA estima a transportadora e o prazo de entrega.">
          <div className="flex gap-2">
            <Input
              value={f.codigoRastreio ?? ''}
              onChange={(e) => set('codigoRastreio', e.target.value)}
              placeholder="Ex.: QB123456789BR"
            />
            <Button
              variant="secondary"
              onClick={calcPrazo}
              disabled={!f.codigoRastreio || track.isPending}
              title="Estimar prazo com IA"
              className="shrink-0"
            >
              {track.isPending ? <Spinner className="h-4 w-4" /> : <Icon name="truck" className="h-4 w-4" />}
              Prazo
            </Button>
          </div>
        </Field>

        {track.data && (
          <p className="text-xs text-muted">
            {track.data.transportadora ? `${track.data.transportadora}. ` : ''}
            {track.data.estimado ? 'Prazo estimado por SLA (sem rastreio em tempo real).' : ''}
            {track.data.detalhe ? ` ${track.data.detalhe}` : ''}
          </p>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field label="Transportadora">
            <Select
              value={f.transportadora ?? ''}
              onChange={(e) => set('transportadora', e.target.value || undefined)}
            >
              <option value="">—</option>
              {SHIPPING_CARRIERS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Prazo de entrega">
            <Input
              type="date"
              value={f.prazoEntrega ?? ''}
              onChange={(e) => set('prazoEntrega', e.target.value || undefined)}
            />
          </Field>
        </div>

        <Field label="Status">
          <Select
            value={f.status ?? 'SOLICITADA'}
            onChange={(e) => set('status', e.target.value as PartRequestStatus)}
          >
            {PART_REQUEST_STATUS.map((s) => (
              <option key={s} value={s}>
                {partRequestStatusMeta[s].label}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Documentos"
          hint="Invoice do fornecedor e nota fiscal de entrada no Brasil (PDF ou imagem)."
        >
          <div className="flex flex-wrap gap-2">
            <DocUpload tipo="INVOICE" label="Invoice" onAdd={addDoc} onExtract={extractFromInvoice} />
            <DocUpload tipo="NOTA_FISCAL" label="Nota fiscal (BR)" onAdd={addDoc} />
          </div>
          {invoice.isPending && (
            <p className="text-xs text-muted flex items-center gap-1 mt-1">
              <Spinner className="h-3 w-3" /> Lendo a invoice com IA…
            </p>
          )}
          {invoice.data &&
            (invoice.data.orderId || invoice.data.valor != null || invoice.data.frete != null) && (
              <p className="text-xs text-muted mt-1">
                IA leu:{' '}
                {[
                  invoice.data.orderId && `pedido ${invoice.data.orderId}`,
                  invoice.data.valor != null &&
                    `valor ${formatMoney(invoice.data.valor, invoice.data.moeda)}`,
                  invoice.data.frete != null &&
                    `frete ${formatMoney(invoice.data.frete, invoice.data.moeda)}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                .
              </p>
            )}
          {(f.documentos?.length ?? 0) > 0 && (
            <ul className="mt-2 space-y-1 text-sm">
              {f.documentos!.map((d, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="text-xs text-muted shrink-0">{partDocTypeLabel[d.tipo]}:</span>
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-accent hover:underline truncate flex-1"
                  >
                    {d.nome ?? 'arquivo'}
                  </a>
                  <button
                    onClick={() => removeDoc(i)}
                    className="text-muted hover:text-st-danger px-1"
                    aria-label="Remover documento"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Field>

        <Field label="Observações">
          <Textarea
            value={f.observacoes ?? ''}
            onChange={(e) => set('observacoes', e.target.value)}
          />
        </Field>
          </>
        )}
      </div>
    </Modal>
  );
}

/** Busca uma peça já comprada (em trânsito) em outra OS e move pra esta. */
function FindBoughtPart({ osId, onMoved }: { osId: string; onMoved: () => void }) {
  const { data } = usePartRequests();
  const move = useMovePartRequest(osId);
  const [q, setQ] = useState('');
  const bought = (data ?? []).filter(
    (p) =>
      p.osId !== osId &&
      (p.status === 'PEDIDA' || p.status === 'EM_TRANSITO') &&
      !p.emEstoque,
  );
  const needle = q.trim().toLowerCase();
  const list = needle
    ? bought.filter((p) =>
        `${p.titulo ?? ''} ${p.orderId ?? ''} ${p.codigoRastreio ?? ''} ${p.osTitulo} ${p.vehicleApelido ?? ''}`
          .toLowerCase()
          .includes(needle),
      )
    : bought;
  return (
    <div className="space-y-3">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar peça comprada (título, pedido, rastreio, OS)…"
      />
      {list.length === 0 ? (
        <p className="text-sm text-muted">Nenhuma peça comprada (Comprada/Em trânsito) em outra OS.</p>
      ) : (
        <ul className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {list.map((p) => {
            const meta = partRequestStatusMeta[p.status];
            return (
              <li key={p.id} className="card p-2.5 flex items-center gap-3">
                <div className="h-9 w-9 rounded bg-surface-2 overflow-hidden grid place-items-center shrink-0 border border-border">
                  {p.fotoUrl ? (
                    <img src={p.fotoUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Icon name="box" className="h-4 w-4 text-muted" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.titulo ?? 'Peça'}</div>
                  <div className="text-xs text-muted truncate flex items-center gap-1.5">
                    <Badge color={meta.color}>{meta.label}</Badge>
                    {p.vehicleApelido ? `${p.vehicleApelido} · ` : ''}
                    {p.osTitulo}
                    {p.orderId ? ` · #${p.orderId}` : ''}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={move.isPending}
                  className="shrink-0"
                  onClick={async () => {
                    await move.mutateAsync({ fromOsId: p.osId, prId: p.id });
                    onMoved();
                  }}
                >
                  Mover pra cá
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Lança uma peça ENTREGUE no estoque (cria item de inventário). */
export function StockModal({
  osId,
  pr,
  open,
  onClose,
}: {
  osId: string;
  pr: PartRequest;
  open: boolean;
  onClose: () => void;
}) {
  const toStock = usePartRequestToStock(osId);
  const [sku, setSku] = useState('');
  const [local, setLocal] = useState('');
  const [min, setMin] = useState('');
  useEffect(() => {
    if (open) {
      setSku('');
      setLocal('');
      setMin('');
    }
  }, [open]);
  const submit = async () => {
    await toStock.mutateAsync({
      prId: pr.id,
      input: {
        sku: sku.trim() || undefined,
        localizacao: local.trim() || undefined,
        estoqueMinimo: min ? Math.max(0, Number(min) || 0) : undefined,
      },
    });
    onClose();
  };
  const custo = pr.valorEntregue ?? pr.valor;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Colocar no estoque"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={toStock.isPending}>
            {toStock.isPending ? 'Lançando…' : 'Lançar no estoque'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          Cria um item de inventário a partir de <b>{pr.titulo ?? 'esta peça'}</b>. Dá pra ajustar os
          detalhes depois em Estoque.
        </p>
        <Field label="SKU" hint="Em branco gera um automático.">
          <Input
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            placeholder={`EST-${pr.id.slice(0, 6).toUpperCase()}`}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Localização">
            <Input
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              placeholder="Ex.: Prateleira A3"
            />
          </Field>
          <Field label="Estoque mínimo">
            <Input type="number" min={0} value={min} onChange={(e) => setMin(e.target.value)} placeholder="0" />
          </Field>
        </div>
        <div className="text-xs text-muted">
          Quantidade: <span className="num">{pr.quantidade}</span>
          {custo != null ? ` · Custo unit.: ${formatMoney(custo, pr.moeda)}` : ''}
        </div>
      </div>
    </Modal>
  );
}

/* ----------------------------- Detail modal ------------------------------ */
function OrderDetailModal({
  id,
  vehicleName,
  onClose,
}: {
  id: string;
  vehicleName: Map<string, string>;
  onClose: () => void;
}) {
  const { data: o, isLoading } = useServiceOrder(id);
  const { data: parts } = useParts();
  const change = useChangeOrderStatus();
  const update = useUpdateOrder();
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const onPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadOrderPhoto(id, file);
      await qc.invalidateQueries({ queryKey: qk.order(id) });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={o?.titulo ?? 'OS'} wide>
      {isLoading || !o ? (
        <Loading />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge color={osStatusMeta[o.status].color}>{osStatusMeta[o.status].label}</Badge>
            <Badge color={priorityMeta[o.prioridade].color}>{priorityMeta[o.prioridade].label}</Badge>
            <span className="text-sm text-muted">{vehicleName.get(o.vehicleId) ?? o.vehicleId}</span>
            <span className="text-sm text-muted num ml-auto">{pluralDays(o.diasParado)} parado</span>
          </div>

          {o.descricao && <p className="text-sm text-muted">{o.descricao}</p>}

          {/* Data de abertura (define os dias parado) */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-xs uppercase tracking-wide text-muted shrink-0">
              Data de abertura
            </span>
            <Input
              type="date"
              className="w-auto py-1 px-2"
              value={o.abertaEm.slice(0, 10)}
              disabled={update.isPending}
              onChange={(e) =>
                e.target.value &&
                update.mutate({ id, input: { abertaEm: `${e.target.value}T12:00:00.000Z` } })
              }
            />
            <span className="text-muted num">→ {pluralDays(o.diasParado)} parado</span>
          </div>

          {/* Mudar status */}
          <div>
            <div className="text-xs uppercase tracking-wide text-muted mb-2">Mudar status</div>
            <div className="flex flex-wrap gap-2">
              {COLUMNS.concat(['CANCELADA']).map((st) => (
                <Button
                  key={st}
                  size="sm"
                  variant={st === o.status ? 'primary' : 'secondary'}
                  onClick={() => change.mutate({ id, status: st })}
                >
                  {osStatusMeta[st].label}
                </Button>
              ))}
            </div>
          </div>

          {/* Checklist */}
          <ChecklistSection order={o} />

          {/* Comentários da OS */}
          <CommentsSection order={o} />

          {/* Produtos e peças utilizados (match com inventário) */}
          {parts && (
            <>
              <UsedItemsSection order={o} parts={parts} tipo="PRODUTO" label="Produtos utilizados" categoria="Consumível" />
              <UsedItemsSection order={o} parts={parts} tipo="PECA" label="Peças utilizadas" categoria="Peça" />
            </>
          )}

          {/* Solicitação de peças (compra externa + rastreio) */}
          <PartRequestSection order={o} />

          {/* Fotos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wide text-muted">Fotos</div>
              <label className="text-xs text-accent cursor-pointer hover:underline">
                {uploading ? 'Enviando…' : '+ Adicionar foto'}
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} />
              </label>
            </div>
            {o.fotos.length === 0 ? (
              <p className="text-sm text-muted">Sem fotos.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {o.fotos.map((f, i) => (
                  <img key={i} src={f.url} alt={f.legenda ?? ''} className="rounded-lg aspect-square object-cover bg-surface-2" />
                ))}
              </div>
            )}
          </div>

          {/* Timeline */}
          <div>
            <div className="text-xs uppercase tracking-wide text-muted mb-2">Timeline</div>
            <ol className="space-y-2 border-l border-border pl-4">
              {o.timeline.map((t, i) => (
                <li key={i} className="text-sm relative">
                  <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-accent" />
                  <div>{t.evento}</div>
                  <div className="text-xs text-muted">
                    {formatDateTime(t.em)} {t.por ? `· ${t.por}` : ''}
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="text-xs text-muted">Aberta em {formatDate(o.abertaEm)}</div>
        </div>
      )}
    </Modal>
  );
}

/* ----------------------------- Create modal ------------------------------ */
export function CreateOrderModal({
  open,
  onClose,
  vehicleId,
}: {
  open: boolean;
  onClose: () => void;
  /** Quando informado (ex.: tela do carro), já vincula a OS a este veículo. */
  vehicleId?: string;
}) {
  const { data: vehicles } = useVehicles();
  const { data: users } = useUsers();
  const create = useCreateOrder();
  const [form, setForm] = useState<CreateServiceOrderInput>({
    vehicleId: vehicleId ?? '',
    titulo: '',
    prioridade: 'MEDIA',
  });
  const set = (k: keyof CreateServiceOrderInput, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  const mechanics = (users ?? []).filter((u) => u.papel === 'MECANICO' || u.papel === 'GERENTE');

  // Ao abrir a partir de um carro, garante o vínculo com o veículo.
  useEffect(() => {
    if (open && vehicleId) setForm((f) => ({ ...f, vehicleId }));
  }, [open, vehicleId]);

  const lockedVehicle = vehicleId ? (vehicles ?? []).find((v) => v.id === vehicleId) : undefined;

  const submit = async () => {
    await create.mutateAsync(form);
    onClose();
    setForm({ vehicleId: vehicleId ?? '', titulo: '', prioridade: 'MEDIA' });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nova ordem de serviço"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={!form.vehicleId || !form.titulo || create.isPending}>
            Abrir OS
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Veículo">
          {vehicleId ? (
            <Input
              value={
                lockedVehicle
                  ? `${lockedVehicle.apelido} — ${lockedVehicle.marca} ${lockedVehicle.modelo}`
                  : '—'
              }
              disabled
              readOnly
            />
          ) : (
            <Select value={form.vehicleId} onChange={(e) => set('vehicleId', e.target.value)}>
              <option value="">Selecione…</option>
              {(vehicles ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.apelido} — {v.marca} {v.modelo}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Título">
          <Input value={form.titulo} onChange={(e) => set('titulo', e.target.value)} placeholder="Diagnóstico elétrico" />
        </Field>
        <Field label="Descrição">
          <Textarea value={form.descricao ?? ''} onChange={(e) => set('descricao', e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Prioridade">
            <Select value={form.prioridade} onChange={(e) => set('prioridade', e.target.value as Priority)}>
              {PRIORITY.map((p) => (
                <option key={p} value={p}>{priorityMeta[p].label}</option>
              ))}
            </Select>
          </Field>
          <Field label="Mecânico">
            <Select
              value={form.mecanicosResponsaveis?.[0] ?? ''}
              onChange={(e) => set('mecanicosResponsaveis', e.target.value ? [e.target.value] : [])}
            >
              <option value="">—</option>
              {mechanics.map((m) => (
                <option key={m.id} value={m.id}>{m.nome}</option>
              ))}
            </Select>
          </Field>
        </div>
        <Field
          label="Data de abertura"
          hint="Em branco = hoje. Os dias parado contam a partir desta data."
        >
          <Input
            type="date"
            value={form.abertaEm ? form.abertaEm.slice(0, 10) : ''}
            onChange={(e) =>
              set('abertaEm', e.target.value ? `${e.target.value}T12:00:00.000Z` : undefined)
            }
          />
        </Field>
      </div>
    </Modal>
  );
}
